/**
 * useExport Composable
 * Handles all export functionality for Tax Dashboard
 */

import { ref } from 'vue';
import ExcelJS from 'exceljs';
import { exportToCSV } from '@/utils/csvExport';
import mockData from '@/services/mock/taxDashboardMockData.json';

export function useExport() {
  const loading = ref(false);

  // Helper function to style header row
  const styleHeaderRow = (worksheet, headerRow) => {
    headerRow.eachCell((cell) => {
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FF185C37' }
      };
      cell.font = {
        bold: true,
        color: { argb: 'FFFFFFFF' },
        size: 12
      };
      cell.alignment = { horizontal: 'center', vertical: 'middle' };
      cell.border = {
        top: { style: 'thin', color: { argb: 'FF000000' } },
        left: { style: 'thin', color: { argb: 'FF000000' } },
        bottom: { style: 'thin', color: { argb: 'FF000000' } },
        right: { style: 'thin', color: { argb: 'FF000000' } }
      };
    });
  };

  // Helper function to style data rows
  const styleDataRows = (worksheet, startRow, endRow) => {
    for (let i = startRow; i <= endRow; i++) {
      const row = worksheet.getRow(i);
      row.eachCell((cell) => {
        cell.border = {
          top: { style: 'thin', color: { argb: 'FFE0E0E0' } },
          left: { style: 'thin', color: { argb: 'FFE0E0E0' } },
          bottom: { style: 'thin', color: { argb: 'FFE0E0E0' } },
          right: { style: 'thin', color: { argb: 'FFE0E0E0' } }
        };
        cell.alignment = { vertical: 'middle' };
      });
      // Alternate row colors
      if (i % 2 === 0) {
        row.eachCell((cell) => {
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFF9F9F9' }
          };
        });
      }
    }
  };

  // Helper function to download Excel file
  const downloadExcel = async (workbook, filename) => {
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  /**
   * Export Access Channels data
   */
  const exportAccessChannels = (data) => {
    const headers = [
      { key: 'label', label: 'Channel' },
      { key: 'value', label: 'Count' },
      { key: 'percentage', label: 'Percentage (%)' }
    ];
    exportToCSV(data, headers, 'access_channels');
  };

  /**
   * Export New vs Returning Users data
   */
  const exportNewVsReturningUsers = (data) => {
    const headers = [
      { key: 'label', label: 'User Type' },
      { key: 'value', label: 'Count' },
      { key: 'percentage', label: 'Percentage (%)' }
    ];
    exportToCSV(data, headers, 'new_vs_returning_users');
  };

  /**
   * Export Usage Funnel by Form data
   */
  const exportUsageFunnelByForm = (data) => {
    const headers = [
      { key: 'stage', label: 'State' },
      { key: 'completeForms', label: 'Complete - Usage' },
      { key: 'completeUniqueUsers', label: 'Complete - Unique User' },
      { key: 'pendingForms', label: 'Pending - Usage' },
      { key: 'pendingUniqueUsers', label: 'Pending - Unique User' },
    ];
    // null pending = the state has no pending notion — export as "-" like the table renders it.
    const rows = (data || []).map((item) => ({
      ...item,
      pendingForms: item.pendingForms ?? '-',
      pendingUniqueUsers: item.pendingUniqueUsers ?? '-',
    }));
    exportToCSV(rows, headers, 'usage_funnel_by_form');
  };

  /**
   * Export Product CTR data
   */
  const exportProductCtr = (data) => {
    const headers = [
      { key: 'product', label: 'Product' },
      { key: 'productType', label: 'Type' },
      { key: 'recommendations', label: '# Recommendations' },
      { key: 'clicks', label: 'Clicks' },
      { key: 'ctr', label: 'CTR (%)' }
    ];
    const sortedData = [...data].sort((a, b) => b.ctr - a.ctr);
    exportToCSV(sortedData, headers, 'product_ctr');
  };

  /**
   * Export Customer Income Distribution data
   */
  const exportCustomerIncome = (data) => {
    // Merge customer income data with fixed income ranges from mock data
    const displayData = mockData.incomeRanges.map((fixed, idx) => {
      const dataItem = data[idx];
      if (!dataItem) {
        return {
          ...fixed,
          totalUsers: 0,
          ageLessThan30: 0,
          age30to44: 0,
          age45to54: 0,
          ageGreaterEqual55: 0
        };
      }
      return {
        ...fixed,
        ...dataItem
      };
    });

    const headers = [
      { key: 'incomeRange', label: 'Income per Year' },
      { key: 'taxRate', label: 'Tax Rate' },
      { key: 'totalUsers', label: 'Total Users' },
      { key: 'ageLessThan30', label: '< 30' },
      { key: 'age30to44', label: '30-44' },
      { key: 'age45to54', label: '45-54' },
      { key: 'ageGreaterEqual55', label: '>= 55' }
    ];
    exportToCSV(displayData, headers, 'customer_income_distribution');
  };

  /**
   * Export Customer Satisfaction data
   */
  const exportSatisfactionData = (satisfactionData, dislikeReasons) => {
    // Export satisfaction summary
    const summaryData = [
      {
        metric: 'Like',
        value: satisfactionData.like,
        percentage: satisfactionData.likePercentage
      },
      {
        metric: 'Dislike',
        value: satisfactionData.dislike,
        percentage: satisfactionData.dislikePercentage
      }
    ];

    const summaryHeaders = [
      { key: 'metric', label: 'Metric' },
      { key: 'value', label: 'Count' },
      { key: 'percentage', label: 'Percentage (%)' }
    ];

    exportToCSV(summaryData, summaryHeaders, 'customer_satisfaction_summary');

    // Also export dislike reasons if available
    if (dislikeReasons && dislikeReasons.length > 0) {
      const reasonsHeaders = [
        { key: 'reason', label: 'Reason' },
        { key: 'count', label: 'Count' }
      ];
      exportToCSV(dislikeReasons, reasonsHeaders, 'customer_satisfaction_dislike_reasons');
    }
  };

  /**
   * Export All Data to Excel
   */
  const exportAllData = async ({
    accessChannelBreakdownPie,
    newVsReturningUsersPie,
    usageFunnelByForm,
    productCtrData,
    customerIncomeData,
    startDate,
    endDate,
    dateRangeText
  }) => {
    try {
      loading.value = true;
      const workbook = new ExcelJS.Workbook();
      workbook.creator = 'Tax Dashboard';
      workbook.created = new Date();

      // Sheet 1: Access Channels
      const accessChannelsSheet = workbook.addWorksheet('Access Channels');
      accessChannelsSheet.columns = [
        { header: 'Channel', key: 'label', width: 30 },
        { header: 'Count', key: 'value', width: 15 },
        { header: 'Percentage (%)', key: 'percentage', width: 15 }
      ];
      accessChannelBreakdownPie.forEach(item => {
        accessChannelsSheet.addRow(item);
      });
      styleHeaderRow(accessChannelsSheet, accessChannelsSheet.getRow(1));
      styleDataRows(accessChannelsSheet, 2, accessChannelBreakdownPie.length + 1);

      // Sheet 2: New vs Returning Users
      const newVsReturningSheet = workbook.addWorksheet('New vs Returning Users');
      newVsReturningSheet.columns = [
        { header: 'User Type', key: 'label', width: 30 },
        { header: 'Count', key: 'value', width: 15 },
        { header: 'Percentage (%)', key: 'percentage', width: 15 }
      ];
      newVsReturningUsersPie.forEach(item => {
        newVsReturningSheet.addRow(item);
      });
      styleHeaderRow(newVsReturningSheet, newVsReturningSheet.getRow(1));
      styleDataRows(newVsReturningSheet, 2, newVsReturningUsersPie.length + 1);

      // Sheet 3: Usage Funnel by Form
      const usageFunnelSheet = workbook.addWorksheet('Usage Funnel by Form');
      usageFunnelSheet.columns = [
        { header: 'State', key: 'stage', width: 35 },
        { header: 'Complete - Usage', key: 'completeForms', width: 18 },
        { header: 'Complete - Unique User', key: 'completeUniqueUsers', width: 24 },
        { header: 'Pending - Usage', key: 'pendingForms', width: 18 },
        { header: 'Pending - Unique User', key: 'pendingUniqueUsers', width: 24 }
      ];
      usageFunnelByForm.forEach(item => {
        // null pending = the state has no pending notion — export as "-" like the table renders it.
        usageFunnelSheet.addRow({
          ...item,
          pendingForms: item.pendingForms ?? '-',
          pendingUniqueUsers: item.pendingUniqueUsers ?? '-',
        });
      });
      styleHeaderRow(usageFunnelSheet, usageFunnelSheet.getRow(1));
      styleDataRows(usageFunnelSheet, 2, usageFunnelByForm.length + 1);

      // Sheet 4: Product CTR
      const productCtrSheet = workbook.addWorksheet('CTR by Recommended Products');
      productCtrSheet.columns = [
        { header: 'Product', key: 'product', width: 25 },
        { header: 'Type', key: 'productType', width: 25 },
        { header: '# Recommendations', key: 'recommendations', width: 20 },
        { header: 'Clicks', key: 'clicks', width: 15 },
        { header: 'CTR (%)', key: 'ctr', width: 15 }
      ];
      const sortedProductData = [...productCtrData].sort((a, b) => b.ctr - a.ctr);
      sortedProductData.forEach(item => {
        productCtrSheet.addRow(item);
      });
      styleHeaderRow(productCtrSheet, productCtrSheet.getRow(1));
      styleDataRows(productCtrSheet, 2, sortedProductData.length + 1);

      // Sheet 5: Customer Income Distribution
      const customerIncomeSheet = workbook.addWorksheet('Customer Income Distribution');
      customerIncomeSheet.columns = [
        { header: 'Income per Year', key: 'incomeRange', width: 30 },
        { header: 'Tax Rate', key: 'taxRate', width: 15 },
        { header: 'Total Users', key: 'totalUsers', width: 15 },
        { header: '< 30', key: 'ageLessThan30', width: 15 },
        { header: '30-44', key: 'age30to44', width: 15 },
        { header: '45-54', key: 'age45to54', width: 15 },
        { header: '>= 55', key: 'ageGreaterEqual55', width: 15 }
      ];
      const displayData = mockData.incomeRanges.map((fixed, idx) => {
        const dataItem = customerIncomeData[idx];
        if (!dataItem) {
          return {
            ...fixed,
            totalUsers: 0,
            ageLessThan30: 0,
            age30to44: 0,
            age45to54: 0,
            ageGreaterEqual55: 0
          };
        }
        return {
          ...fixed,
          ...dataItem
        };
      });
      displayData.forEach(item => {
        customerIncomeSheet.addRow(item);
      });
      styleHeaderRow(customerIncomeSheet, customerIncomeSheet.getRow(1));
      styleDataRows(customerIncomeSheet, 2, displayData.length + 1);

      // Download file
      const filename = `Tax_Dashboard_${startDate}_to_${endDate}.xlsx`;
      await downloadExcel(workbook, filename);

      loading.value = false;
    } catch (error) {
      console.error('Error exporting all data:', error);
      loading.value = false;
      throw error;
    }
  };

  return {
    loading,
    exportAccessChannels,
    exportNewVsReturningUsers,
    exportUsageFunnelByForm,
    exportProductCtr,
    exportCustomerIncome,
    exportSatisfactionData,
    exportAllData
  };
}
